/**
 * Invite recipient resolution (#2623).
 *
 * Shared by the web + native `ClubMemberInviteModal` direct-invite field. The
 * single "Email or phone" input mirrors the login magic-link idiom
 * (`LoginModal.tsx` — one field, auto-detect) so a leader can send a single-use
 * invite to either channel without a mode switch.
 *
 * Detection: a value made only of phone characters (digits, `+`, and the usual
 * separators) is treated as a phone and normalized to E.164 per Core Rule 27;
 * anything else is treated as an email. This routes error copy sensibly — a
 * value with letters (`member`) fails as an email, not as a phone.
 *
 * Error messages come from the central validation registry
 * (`validation/rules.ts`) — no new inline strings (#2623 scope item 3).
 *
 * Pure/platform-agnostic: no React, no DOM, no RN — safe to import from both
 * clients (`@shared/lib/inviteRecipient` on native).
 */
import { normalizePhoneE164 } from './phone';
import { validateValue, validationRules } from './validation/rules';

export type InviteRecipient =
  | { kind: 'email'; invitee_email: string }
  | { kind: 'phone'; invitee_phone: string }
  | { kind: 'invalid'; message: string };

/** Phone-shaped: only digits, an optional leading `+`, and separators. */
const PHONE_SHAPE = /^[+]?[\d\s\-().]+$/;

/**
 * Classify a raw recipient string into an `invitee_email` or `invitee_phone`
 * payload fragment, or an `invalid` result carrying the canonical message.
 *
 * The returned payload never contains both fields — the modal spreads exactly
 * one of `invitee_email` / `invitee_phone` into the create call.
 */
export function resolveInviteRecipient(raw: string): InviteRecipient {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { kind: 'invalid', message: validationRules.required.message };
  }

  if (PHONE_SHAPE.test(trimmed)) {
    const normalized = normalizePhoneE164(trimmed);
    if (!normalized) {
      return { kind: 'invalid', message: validationRules.phone.message };
    }
    return { kind: 'phone', invitee_phone: normalized };
  }

  const { isValid } = validateValue(trimmed, 'email');
  if (!isValid) {
    return { kind: 'invalid', message: validationRules.email.message };
  }
  return { kind: 'email', invitee_email: trimmed };
}
